import UsFlagSvg from '@/components/common/UsFlag'
import { useState, useRef } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/lib/utils'
import {
  GraduationCap, Mail, Lock, Eye, EyeOff, Loader2, User, Calendar,
  Users, Baby, Phone, Star, Crown, BookOpen, Mic, Award, ArrowLeft
} from 'lucide-react'

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3000'
const PREMIUM_AMOUNT = 55000

const registerSchema = z.object({
  firstName: z.string().min(2, 'Mínimo 2 caracteres'),
  lastName: z.string().min(2, 'Mínimo 2 caracteres'),
  phone: z.string().min(8, 'Teléfono inválido'),
  email: z.string().email('Email inválido'),
  password: z.string().min(8, 'Mínimo 8 caracteres'),
  confirmPassword: z.string(),
  role: z.enum(['student', 'parent']),
  birthDate: z.string().optional(),
  childBirthDate: z.string().optional()
}).refine(data => data.password === data.confirmPassword, {
  message: 'Las contraseñas no coinciden',
  path: ['confirmPassword']
})

type RegisterForm = z.infer<typeof registerSchema>

export default function Register() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isPremium = searchParams.get('plan') === 'premium'
  const startDateParam = searchParams.get('start_date')
  const scheduleDayParam = searchParams.get('schedule_day')
  const scheduleStartParam = searchParams.get('schedule_start_time')
  const scheduleEndParam = searchParams.get('schedule_end_time')
  const { signUp, user, profile, isLoading: authLoading } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [step, setStep] = useState(1)
  const transbankFormRef = useRef<HTMLFormElement>(null)
  const paymentInProgressRef = useRef(false)

  // ✅ useForm ANTES del early return — regla de hooks
  const { register, handleSubmit, watch, formState: { errors } } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: { role: 'student' }
  })

  const selectedRole = watch('role')

  if (!authLoading && user && profile && !isPremium && !paymentInProgressRef.current) {
    return <Navigate to="/dashboard" replace />
  }

  const onSubmit = async (data: RegisterForm) => {
    setIsLoading(true)
    if (isPremium) {
      paymentInProgressRef.current = true
    }
    try {
      const birthDate = data.role === 'parent' ? data.childBirthDate : data.birthDate
      let ageGroup: 'children' | 'adult' = 'adult'

      if (birthDate) {
        const age = new Date().getFullYear() - new Date(birthDate).getFullYear()
        ageGroup = age >= 6 && age <= 10 ? 'children' : 'adult'
      }

      const { error } = await signUp(data.email, data.password, {
        first_name: data.firstName,
        last_name: data.lastName,
        phone: data.phone,
        role: data.role,
        birth_date: birthDate,
        age_group: ageGroup
      })

      if (error) {
        toast.error(error.message)
      } else if (isPremium) {
        toast.success('Cuenta creada. Redirigiendo al pago...')
        try {
          const { supabase } = await import('@/lib/supabase')
          const { data: { user: newUser } } = await supabase.auth.getUser()
          if (!newUser) throw new Error('No se pudo obtener el usuario')

          // Leer modo de checkout desde app_settings
          const { data: settings } = await supabase
            .from('app_settings')
            .select('key, value')
            .in('key', ['checkout_mode', 'checkout_whatsapp_number', 'checkout_whatsapp_message'])
          const cfg = Object.fromEntries((settings ?? []).map((s: any) => [s.key, s.value]))
          const mode = cfg.checkout_mode ?? '1'

          if (mode === '2') {
            const number = cfg.checkout_whatsapp_number ?? ''
            const template = cfg.checkout_whatsapp_message ?? 'Hola! Acabo de registrarme y quiero coordinar el pago.'
            const msg = template
              .replaceAll('{first_name}', data.firstName)
              .replaceAll('{last_name}', data.lastName)
              .replaceAll('{email}', data.email)
              .replaceAll('{phone}', data.phone)
              .replaceAll('{role}', data.role)
              .replaceAll('{amount}', String(PREMIUM_AMOUNT))
              .replaceAll('{start_date}', startDateParam ?? '')
            toast.success('Cuenta creada. Te redirigimos a WhatsApp...')
            window.location.href = `https://wa.me/${number}?text=${msg}`
            return
          }

          const res = await fetch(`${BACKEND_URL}/api/transbank/create`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              amount: PREMIUM_AMOUNT,
              user_id: newUser.id,
              start_date: startDateParam,
              schedule_day: scheduleDayParam,
              schedule_start_time: scheduleStartParam,
              schedule_end_time: scheduleEndParam
            })
          })
          const payData = await res.json()
          if (!res.ok) throw new Error(payData.error || 'Error al crear el pago')

          if (transbankFormRef.current) {
            const form = transbankFormRef.current
            form.action = payData.url
            const tokenInput = form.querySelector('input[name="token_ws"]') as HTMLInputElement
            tokenInput.value = payData.token
            form.submit()
            return
          }
        } catch (payErr: any) {
          console.error('Error al iniciar pago:', payErr)
          toast.error('Error al conectar con el pago. Redirigiendo a planes...')
          navigate('/planes')
        }
      } else {
        toast.success(t('auth.registerSuccess'))
        navigate('/dashboard')
      }
    } catch {
      toast.error(t('errors.generic'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      {/* Hidden form for Transbank POST redirect */}
      <form ref={transbankFormRef} method="POST" style={{ display: 'none' }}>
        <input type="hidden" name="token_ws" value="" />
      </form>

      <div className="min-h-screen flex">
        {/* Panel izquierdo - Formulario */}
        <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-8 py-12 overflow-y-auto">
          <div className="w-full max-w-md mx-auto">

            <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors mb-6">
              <ArrowLeft className="w-4 h-4" />
              Volver al inicio
            </Link>

            <Link to="/" className="flex items-center gap-2 mb-8">
              <GraduationCap className="w-10 h-10 text-primary" />
              <span className="text-2xl font-bold">
                Nomad<span className="text-[#EF4135]">Lexis</span>
              </span>
            </Link>

            {isPremium && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-amber-700 text-sm font-semibold rounded-full mb-4">
                <Crown className="w-4 h-4" />
                Programa Completo
              </div>
            )}
            <h1 className="text-3xl font-bold mb-2">
              {isPremium ? 'Inscríbete al programa' : 'Crear una cuenta'}
            </h1>
            <p className="text-muted-foreground mb-8">
              {isPremium
                ? 'Crea tu cuenta para acceder al programa completo de Inglés Aplicado'
                : 'Comienza tu prueba gratis de 7 días'}
            </p>

            {/* Progress */}
            <div className="flex gap-2 mb-8">
              <div className={cn('flex-1 h-2 rounded-full', step >= 1 ? (isPremium ? 'bg-amber-500' : 'bg-primary') : 'bg-muted')} />
              <div className={cn('flex-1 h-2 rounded-full', step >= 2 ? (isPremium ? 'bg-amber-500' : 'bg-primary') : 'bg-muted')} />
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {step === 1 && (
                <>
                  {/* Selección de rol — span porque apunta a un div, no a un input */}
                  <div>
                    <span className="block text-sm font-medium mb-3">
                      {t('auth.chooseRole')}
                    </span>
                    <div className="grid grid-cols-2 gap-4">
                      <label className={cn(
                        'flex flex-col items-center gap-2 p-4 rounded-lg border-2 cursor-pointer transition-colors',
                        selectedRole === 'student' ? 'border-primary bg-primary/5' : 'border-muted hover:border-primary/50'
                      )}>
                        <input type="radio" value="student" {...register('role')} className="sr-only" />
                        <Users className="w-8 h-8 text-primary" />
                        <span className="font-medium">{t('auth.student')}</span>
                      </label>
                      <label className={cn(
                        'flex flex-col items-center gap-2 p-4 rounded-lg border-2 cursor-pointer transition-colors',
                        selectedRole === 'parent' ? 'border-primary bg-primary/5' : 'border-muted hover:border-primary/50'
                      )}>
                        <input type="radio" value="parent" {...register('role')} className="sr-only" />
                        <Baby className="w-8 h-8 text-primary" />
                        <span className="font-medium">{t('auth.parent')}</span>
                      </label>
                    </div>
                  </div>

                  {/* Nombre y Apellido */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="reg-first-name" className="block text-sm font-medium mb-2">
                        {t('auth.firstName')}
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <input
                          id="reg-first-name"
                          type="text"
                          {...register('firstName')}
                          className={cn(
                            'w-full pl-10 pr-4 py-3 rounded-lg border bg-background',
                            'focus:outline-none focus:ring-2 focus:ring-primary',
                            errors.firstName && 'border-red-500'
                          )}
                          placeholder="Jean"
                        />
                      </div>
                      {errors.firstName && <p className="text-sm text-red-500 mt-1">{errors.firstName.message}</p>}
                    </div>
                    <div>
                      <label htmlFor="reg-last-name" className="block text-sm font-medium mb-2">
                        {t('auth.lastName')}
                      </label>
                      <input
                        id="reg-last-name"
                        type="text"
                        {...register('lastName')}
                        className={cn(
                          'w-full px-4 py-3 rounded-lg border bg-background',
                          'focus:outline-none focus:ring-2 focus:ring-primary',
                          errors.lastName && 'border-red-500'
                        )}
                        placeholder="Dupont"
                      />
                      {errors.lastName && <p className="text-sm text-red-500 mt-1">{errors.lastName.message}</p>}
                    </div>
                  </div>

                  {/* Teléfono */}
                  <div>
                    <label htmlFor="reg-phone" className="block text-sm font-medium mb-2">
                      Teléfono
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <input
                        id="reg-phone"
                        type="tel"
                        {...register('phone')}
                        className={cn(
                          'w-full pl-10 pr-4 py-3 rounded-lg border bg-background',
                          'focus:outline-none focus:ring-2 focus:ring-primary',
                          errors.phone && 'border-red-500'
                        )}
                        placeholder="+56 9 1234 5678"
                      />
                    </div>
                    {errors.phone && <p className="text-sm text-red-500 mt-1">{errors.phone.message}</p>}
                  </div>

                  {/* Fecha de nacimiento (student) */}
                  {selectedRole === 'student' && (
                    <div>
                      <label htmlFor="reg-birth-date" className="block text-sm font-medium mb-2">
                        {t('auth.birthDate')}
                      </label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <input
                          id="reg-birth-date"
                          type="date"
                          {...register('birthDate')}
                          className="w-full pl-10 pr-4 py-3 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>
                  )}

                  {/* Fecha de nacimiento del hijo (parent) */}
                  {selectedRole === 'parent' && (
                    <div>
                      <label htmlFor="reg-child-birth-date" className="block text-sm font-medium mb-2">
                        {t('auth.childAge')}
                      </label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <input
                          id="reg-child-birth-date"
                          type="date"
                          {...register('childBirthDate')}
                          className="w-full pl-10 pr-4 py-3 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">Sección niños: 6-10 años</p>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className={cn(
                      'w-full py-3 rounded-lg font-medium transition-colors',
                      isPremium ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-primary text-primary-foreground hover:bg-primary/90'
                    )}
                  >
                    {t('common.continue')}
                  </button>
                </>
              )}

              {step === 2 && (
                <>
                  {/* Email */}
                  <div>
                    <label htmlFor="reg-email" className="block text-sm font-medium mb-2">
                      {t('auth.email')}
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <input
                        id="reg-email"
                        type="email"
                        {...register('email')}
                        className={cn(
                          'w-full pl-10 pr-4 py-3 rounded-lg border bg-background',
                          'focus:outline-none focus:ring-2 focus:ring-primary',
                          errors.email && 'border-red-500'
                        )}
                        placeholder="email@exemple.com"
                      />
                    </div>
                    {errors.email && <p className="text-sm text-red-500 mt-1">{errors.email.message}</p>}
                  </div>

                  {/* Password */}
                  <div>
                    <label htmlFor="reg-password" className="block text-sm font-medium mb-2">
                      {t('auth.password')}
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <input
                        id="reg-password"
                        type={showPassword ? 'text' : 'password'}
                        {...register('password')}
                        className={cn(
                          'w-full pl-10 pr-12 py-3 rounded-lg border bg-background',
                          'focus:outline-none focus:ring-2 focus:ring-primary',
                          errors.password && 'border-red-500'
                        )}
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {errors.password && <p className="text-sm text-red-500 mt-1">{errors.password.message}</p>}
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label htmlFor="reg-confirm-password" className="block text-sm font-medium mb-2">
                      {t('auth.confirmPassword')}
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <input
                        id="reg-confirm-password"
                        type={showPassword ? 'text' : 'password'}
                        {...register('confirmPassword')}
                        className={cn(
                          'w-full pl-10 pr-4 py-3 rounded-lg border bg-background',
                          'focus:outline-none focus:ring-2 focus:ring-primary',
                          errors.confirmPassword && 'border-red-500'
                        )}
                        placeholder="••••••••"
                      />
                    </div>
                    {errors.confirmPassword && <p className="text-sm text-red-500 mt-1">{errors.confirmPassword.message}</p>}
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="flex-1 py-3 border rounded-lg font-medium hover:bg-muted transition-colors"
                    >
                      {t('common.back')}
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className={cn(
                        'flex-1 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2',
                        isPremium ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-primary text-primary-foreground hover:bg-primary/90'
                      )}
                    >
                      {isLoading && <Loader2 className="w-5 h-5 animate-spin" />}
                      {isPremium ? 'Crear cuenta y continuar al pago' : t('auth.signUp')}
                    </button>
                  </div>
                </>
              )}
            </form>

            <p className="text-center mt-6 text-muted-foreground">
              {t('auth.hasAccount')}{' '}
              <Link to="/login" className="text-primary font-medium hover:underline">
                {t('auth.signIn')}
              </Link>
            </p>

            {isPremium && (
              <p className="text-center mt-3 text-xs text-muted-foreground">
                ¿Solo quieres probar?{' '}
                <Link to="/register" className="text-primary hover:underline">
                  Comienza con la prueba gratuita
                </Link>
              </p>
            )}
          </div>
        </div>

        {/* Panel derecho - Decorativo */}
        {isPremium ? (
          <div className="hidden lg:flex flex-1 bg-gradient-to-br from-amber-50 via-amber-100 to-amber-50 items-center justify-center p-8">
            <div className="max-w-md w-full">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-500 rounded-2xl mb-4 shadow-lg">
                  <Crown className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Programa Completo</h2>
                <p className="text-amber-700 font-medium">Inglés Aplicado — Todo incluido</p>
              </div>

              <div className="bg-white/80 backdrop-blur rounded-2xl p-6 shadow-sm border border-amber-200 mb-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-500" />
                  Lo que incluye tu programa
                </h3>
                <ul className="space-y-3">
                  {[
                    { icon: BookOpen, text: '4 unidades · 16 lecciones · 64 contenidos' },
                    { icon: GraduationCap, text: '384 ejercicios interactivos' },
                    { icon: Mic, text: '4 talleres en vivo con profesor' },
                    { icon: Award, text: '4 exámenes + Certificado de finalización' },
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <item.icon className="w-4 h-4 text-amber-600" />
                      </div>
                      <span className="text-gray-700 text-sm leading-relaxed pt-1">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white/80 backdrop-blur rounded-2xl p-6 shadow-sm border border-amber-200 text-center">
                <p className="text-sm text-gray-500 mb-1">Precio único</p>
                <p className="text-4xl font-bold text-gray-900">$55.000 <span className="text-base font-normal text-gray-500">CLP</span></p>
                <p className="text-sm text-amber-600 mt-2 font-medium">Pago seguro con Transbank tras crear tu cuenta</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="hidden lg:flex flex-1 bg-gradient-to-br from-[#B22234] via-white to-[#3C3B6E] items-center justify-center">
            <div className="text-center max-w-md">
              <div className="flex justify-center mb-8">
                <UsFlagSvg />
              </div>
              <h2 className="text-3xl font-bold text-[#3C3B6E] mb-4">¡7 días gratis!</h2>
              <ul className="text-left space-y-3 text-gray-700">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#0055A4] rounded-full" />
                  2 lecciones completas
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-white border rounded-full" />
                  Ejercicios interactivos
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#EF4135] rounded-full" />
                  1 taller en vivo
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
